// Optional starter templates. Unlike WORKFLOW_DEFINITIONS these are not
// materialised or enabled for every business. An owner explicitly copies one
// into an editable draft, reviews the wording and service scope, publishes it,
// and then separately turns it on.

import {
  condition,
  decision,
  delay,
  execution,
  trigger,
  type WorkflowDefinition,
} from "./schema.ts";

export type WorkflowRecipeVertical = "elective_clinic" | "ktv" | "general";

export interface WorkflowRecipe extends WorkflowDefinition {
  vertical: WorkflowRecipeVertical;
  outcome: "convert" | "care" | "recover" | "learn";
  safetyNote: string;
}

export const CLINIC_CONSULTATION_FOLLOW_UP: WorkflowRecipe = {
  id: "clinic-consultation-follow-up",
  key: "recipe_clinic_consultation_follow_up",
  name: "Consultation or quote follow-up",
  description:
    "Two days after a first consultation or visit, offers a calm next-step conversation when the customer has not already replied.",
  version: 1,
  vertical: "elective_clinic",
  outcome: "convert",
  safetyNote: "No diagnosis, outcome promise, discount or artificial urgency is included.",
  nodes: [
    trigger({
      id: "trigger",
      event: "trial_or_first_visit_completed",
      label: "First consultation or visit is completed",
      description: "Starts only after staff mark the customer's first visit as completed.",
    }),
    delay({
      id: "wait-two-days",
      mode: "duration",
      durationMinutes: 2 * 24 * 60,
      label: "Wait 2 days",
      description: "Leaves space for the customer to consider their next step.",
    }),
    condition({
      id: "still-quiet",
      label: "Only if they are still quiet",
      description: "Ends when the customer has already replied after the visit.",
      exceptions: [{
        label: "The customer already replied",
        field: "conversation.hasReplied",
        operator: "is_true",
      }],
    }),
    execution({
      id: "send-follow-up",
      action: "send_message",
      emailFallback: true,
      message: {
        source: "static",
        value:
          "Hi {{customer_name}}, thank you for visiting us for {{listing_name}}. If you'd like to discuss the next step or the options available, reply here and our team will help. There's no pressure to decide today.",
      },
      label: "Offer help with the next step",
      description: "Sends one factual, low-pressure follow-up in the existing chat or by booking email.",
    }),
  ],
};

export const CLINIC_POST_TREATMENT_CHECK_IN: WorkflowRecipe = {
  id: "clinic-post-treatment-check-in",
  key: "recipe_clinic_post_treatment_check_in",
  name: "Human-reviewed service check-in",
  description:
    "Prepares a human-reviewed check-in one day after a completed service so questions return to the right person.",
  version: 1,
  vertical: "elective_clinic",
  outcome: "care",
  safetyNote: "Every message waits for human approval; urgent health or safety concerns stay with people.",
  nodes: [
    trigger({
      id: "trigger",
      event: "booking_completed",
      label: "A service visit is completed",
      description: "Starts after staff mark the relevant appointment as completed.",
    }),
    delay({
      id: "wait-one-day",
      mode: "duration",
      durationMinutes: 24 * 60,
      label: "Wait 1 day",
      description: "Checks in the next day rather than immediately after the visit.",
    }),
    execution({
      id: "send-check-in",
      action: "send_message",
      requiresApproval: true,
      message: {
        source: "static",
        value:
          "Hi {{customer_name}}, we're checking in after your recent {{listing_name}} visit. If you have a question or anything needs attention, reply here and a person from our team will follow up. For urgent health or safety concerns, please contact the appropriate local service.",
      },
      label: "Human reviews the check-in",
      description: "Places the message in the approval queue before anything is sent.",
    }),
  ],
};

export const CLINIC_PRIVATE_EXPERIENCE_FEEDBACK: WorkflowRecipe = {
  id: "clinic-private-experience-feedback",
  key: "recipe_clinic_private_experience_feedback",
  name: "Private experience feedback",
  description:
    "Three days after a completed visit, collects one private rating and routes poor or safety-sensitive feedback to a person.",
  version: 1,
  vertical: "elective_clinic",
  outcome: "learn",
  safetyNote:
    "Low ratings and health, safety or privacy phrases open a human-owned case; nothing is posted to a public review site.",
  nodes: [
    trigger({
      id: "trigger",
      event: "booking_completed",
      label: "A visit is completed",
      description: "Starts only after staff mark the relevant appointment as completed.",
    }),
    delay({
      id: "wait-three-days",
      mode: "duration",
      durationMinutes: 3 * 24 * 60,
      label: "Wait 3 days",
      description: "Leaves time for the customer to reflect on the experience.",
    }),
    execution({
      id: "request-feedback",
      action: "request_feedback",
      emailFallback: true,
      feedbackExpiresDays: 14,
      message: {
        source: "static",
        value:
          "Hi {{customer_name}}, we'd value your private feedback about your recent {{listing_name}} visit. Please use the link below to tell our team what went well or what needs attention. This form is not an emergency service; seek urgent local help if needed.",
      },
      label: "Request private feedback",
      description: "Sends a one-use private form by chat or the booking email.",
    }),
  ],
};

export const CLINIC_CASE_RECOVERY_REMINDER: WorkflowRecipe = {
  id: "clinic-case-recovery-reminder",
  key: "recipe_clinic_case_recovery_reminder",
  name: "Resolved-case follow-up",
  description:
    "One day after a case is resolved, reminds the owner to check that the customer is genuinely okay and the loop is closed.",
  version: 1,
  vertical: "elective_clinic",
  outcome: "recover",
  safetyNote: "The workflow alerts the team only; it never reactivates AI or contacts the customer automatically.",
  nodes: [
    trigger({
      id: "trigger",
      event: "case_resolved",
      label: "A case is resolved",
      description: "Starts only after a person records the case outcome.",
    }),
    delay({
      id: "wait-one-day",
      mode: "duration",
      durationMinutes: 24 * 60,
      label: "Wait 1 day",
      description: "Creates a deliberate next-day recovery checkpoint.",
    }),
    execution({
      id: "notify-owner",
      action: "notify_owner",
      reason:
        "Recovery check for {{case_reference}}: review the recorded outcome and decide whether a personal follow-up with {{customer_name}} is appropriate. Dibs has not contacted the customer.",
      label: "Remind the owner to close the loop",
      description: "Keeps the final relationship-recovery decision with a person.",
    }),
  ],
};

export const KTV_CANCELLATION_RECOVERY: WorkflowRecipe = {
  id: "ktv-cancellation-recovery",
  key: "recipe_ktv_cancellation_recovery",
  name: "Cancellation recovery",
  description:
    "After a cancelled booking, offers one easy route back without discounting or pressuring the customer.",
  version: 1,
  vertical: "ktv",
  outcome: "recover",
  safetyNote: "The message makes no refund, availability or price promise.",
  nodes: [
    trigger({
      id: "trigger",
      event: "booking_cancelled",
      label: "A booking is cancelled",
      description: "Starts when the booking is actually recorded as cancelled.",
    }),
    delay({
      id: "wait-one-hour",
      mode: "duration",
      durationMinutes: 60,
      label: "Wait 1 hour",
      description: "Avoids replying on top of the cancellation itself.",
    }),
    execution({
      id: "send-rebook-help",
      action: "send_message",
      emailFallback: true,
      message: {
        source: "static",
        value:
          "Hi {{customer_name}}, we saw that your {{listing_name}} booking was cancelled. If you'd like a different date or room, reply here and we'll help you check the available options.",
      },
      label: "Offer help to rebook",
      description: "Sends one practical invitation in chat or by booking email.",
    }),
  ],
};

export const KTV_POST_VISIT_FEEDBACK: WorkflowRecipe = {
  id: "ktv-post-visit-feedback",
  key: "recipe_ktv_post_visit_feedback",
  name: "Same-day private feedback",
  description:
    "A few hours after a completed booking, asks what went well or could be better so complaints can enter human recovery quickly.",
  version: 1,
  vertical: "ktv",
  outcome: "learn",
  safetyNote: "Feedback stays conversational; it does not gate, buy or manipulate public reviews.",
  nodes: [
    trigger({
      id: "trigger",
      event: "booking_completed",
      label: "A visit is completed",
      description: "Starts after staff mark the booking completed.",
    }),
    delay({
      id: "wait-two-hours",
      mode: "duration",
      durationMinutes: 2 * 60,
      label: "Wait 2 hours",
      description: "Asks while the visit is still fresh without interrupting it.",
    }),
    execution({
      id: "ask-feedback",
      action: "request_feedback",
      emailFallback: true,
      feedbackExpiresDays: 14,
      message: {
        source: "static",
        value:
          "Hi {{customer_name}}, thanks for visiting us for {{listing_name}}. Please use the private link below to tell us what went well and what we could do better. If something wasn't right, a person from our team will follow up.",
      },
      label: "Ask for private feedback",
      description: "Sends a one-use private form instead of pushing the customer to a public review.",
    }),
  ],
};

export const FIRST_SESSION_FEEDBACK: WorkflowRecipe = {
  id: "first-session-feedback",
  key: "recipe_first_session_feedback",
  name: "Ask for feedback after first session",
  description:
    "An hour after a customer's first session, asks how it went, saves what they say to their profile, alerts you to anything critical, and invites them to leave a Google review.",
  version: 1,
  vertical: "general",
  outcome: "learn",
  safetyNote:
    "Everyone who replies gets the same Google review invitation, whatever they said, so reviews are never gated. Critical feedback alerts you; nothing is promised to the customer.",
  knowledge: {
    instructions:
      "When the customer replies about their session: thank them warmly in one or two sentences. If they raise a problem, apologise, don't promise refunds, discounts or fixes, and say the team will follow up personally. Don't ask for a review yourself; this workflow sends that separately.",
  },
  nodes: [
    trigger({
      id: "trigger",
      event: "session_finished",
      hoursAfter: 1,
      firstSessionOnly: true,
      label: "1 hour after their first session ends",
      description: "Starts once per customer, an hour after their first session here ends. Cancelled and no-show bookings never start it.",
    }),
    execution({
      id: "ask-feedback",
      action: "send_message",
      whatsappTemplateAuto: true,
      message: {
        source: "static",
        value:
          "Hi {{customer_name}}, thanks for coming to your first {{listing_name}} with us! How did you find it? We'd love to hear your thoughts, good or bad.",
      },
      label: "Ask how it went",
      description: "One friendly question in the chat they already use with you.",
    }),
    delay({
      id: "wait-for-reply",
      mode: "until_reply",
      timeoutMinutes: 2 * 24 * 60,
      label: "Wait for their reply",
      description: "Up to 2 days. The AI answers them in the meantime, following this workflow's instructions.",
    }),
    condition({
      id: "replied",
      label: "Only if they replied",
      description: "Ends quietly when they don't reply.",
      exceptions: [{
        label: "NOT Customer has replied",
        field: "conversation.hasReplied",
        operator: "is_false",
      }],
    }),
    decision({
      id: "assess",
      goal:
        "Read the customer's reply about their first session. Summarise their feedback for the team, judge how they felt, and flag anything critical: a safety, health or privacy concern, a serious service failure, or a customer who is angry or asks for a refund.",
      output: [
        { key: "profile_note", type: "string", description: "For the customer's profile: what they said about their first session, quoting their key words, and how they felt. 1-3 sentences." },
        { key: "sentiment", type: "enum", enumValues: ["happy", "neutral", "unhappy"], description: "How they felt about the session." },
        { key: "critical", type: "boolean", description: "True only for a safety, health or privacy concern, a serious service failure, or an angry customer or refund request." },
        { key: "owner_alert", type: "string", description: "1-2 sentences for the owner: what they said and anything they asked for." },
      ],
      label: "Read their feedback",
    }),
    execution({
      id: "save-feedback",
      action: "save_to_profile",
      noteTitle: "Feedback after first session",
      message: { source: "decision", nodeId: "assess", field: "profile_note" },
      label: "Save it to their profile",
      description: "Adds the summary under Notes on their profile.",
    }),
    condition({
      id: "only-if-critical",
      label: "Only if it's critical",
      description: "Feedback that isn't critical goes straight to the review invitation.",
      exceptions: [{
        label: "NOT Read their feedback: critical",
        field: "decision.assess.critical",
        operator: "is_false",
      }],
    }),
    execution({
      id: "alert-owner",
      action: "notify_owner",
      message: { source: "decision", nodeId: "assess", field: "owner_alert" },
      reason:
        "{{customer_name}} left critical feedback after their first {{listing_name}}. Check their profile and follow up.",
      label: "Alert you",
      description: "Emails you the summary so a person can follow up.",
    }),
    execution({
      id: "ask-review",
      action: "send_message",
      message: {
        source: "static",
        value:
          "Thanks for telling us, {{customer_name}}. If you'd like to share your experience with others, you can leave us a Google review here: {{google_review_link}}",
      },
      label: "Invite them to leave a Google review",
      description: "Sent to everyone who replied. Skipped if you haven't added your review link in Connections.",
    }),
  ],
  edges: [{ from: "only-if-critical", to: "ask-review", on: "skip" }],
};

export const WORKFLOW_RECIPES: WorkflowRecipe[] = [
  FIRST_SESSION_FEEDBACK,
  CLINIC_CONSULTATION_FOLLOW_UP,
  CLINIC_POST_TREATMENT_CHECK_IN,
  CLINIC_PRIVATE_EXPERIENCE_FEEDBACK,
  CLINIC_CASE_RECOVERY_REMINDER,
  KTV_CANCELLATION_RECOVERY,
  KTV_POST_VISIT_FEEDBACK,
];

export const CLINIC_WORKFLOW_RECIPES = WORKFLOW_RECIPES.filter(
  (recipe) => recipe.vertical === "elective_clinic",
);

export const KTV_WORKFLOW_RECIPES = WORKFLOW_RECIPES.filter(
  (recipe) => recipe.vertical === "ktv",
);
